import {
  AsyncComponent,
  AuthorizedRoute,
  OnlyPublicRouteCoordination,
} from '@wajeez-web/components/helpers';
import { applyTheme } from '@wajeez-web/core/utils';
import { useCommonSelector } from '@wajeez-web/stores/common';
import { ConfigProvider } from 'antd';
import { useEffect, useMemo } from 'react';

import {
  createBrowserRouter,
  LoaderFunction,
  matchPath,
  Outlet,
  RouteObject,
  RouterProvider,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import NavBar from '../NavBar/NavBar';
import SideBar from '../Sidebar/SideBar';
import { RouteLayoutProps } from './AuthorizedLayout';

import en from 'antd/es/locale/en_US';
import ar from 'antd/es/locale/ar_EG';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { SignInPage } from '@wajeez-web/modules/auth';
import {
  ContainerEndpointItem,
  EndpointItem,
  RouteEndpointItem,
} from '@wajeez-web/core/types';

const loader: (pattern: string) => LoaderFunction =
  (pattern) => async (args) => {
    const path = getRelativeUrl(args.request.url);
    const match = matchPath(
      {
        path: pattern,
        caseSensitive: false,
        end: false,
      },
      path
    );

    return match?.params;
  };

// get url without the domain
const getRelativeUrl = (url: string) => {
  const urlObject = new URL(url);
  return urlObject.pathname;
};

const getRoutes = (routes: EndpointItem[], path: string): RouteObject[] => {
  return routes.map((item) =>
    item.type === 'container'
      ? {
          path: item.endpoint,
          element: <Outlet />,
          handle: item.crump
            ? {
                crumb: item.crump,
              }
            : undefined,
          children: getRoutes(item.children, path + item.endpoint + '/'),
          loader: item.crump ? loader(path + item.endpoint) : undefined,
        }
      : {
          index: item.endpoint === '/',
          path: item.endpoint,
          element: <item.component />,
          handle: item.crump
            ? {
                crumb: item.crump,
              }
            : undefined,
          loader: item.crump ? loader(path + item.endpoint) : undefined,
        }
  );
};

const MainApp = ({
  endpoints,
  routes,
  withSettings = true,
}: RouteLayoutProps & { withSettings?: boolean }) => {
  const { dir, lang } = useCommonSelector((state) => state.common.settings);

  const { i18n } = useTranslation();

  useEffect(() => {
    i18n.changeLanguage(lang);

    moment.locale('ar');
  }, [lang]);

  useEffect(() => {
    applyTheme('base');

    return () => {
      return;
    };
  }, []);

  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          path: '/auth',
          element: <OnlyPublicRouteCoordination />,
          children: [
            {
              path: 'login',
              element: <Outlet />,
              children: [
                {
                  index: true,
                  element: <SignInPage />,
                },
              ],
            },
          ],
        },
        {
          path: 'login',
          element: <Outlet />,
          children: [
            {
              element: <OnlyPublicRouteCoordination />,
              children: [
                {
                  index: true,
                  element: <SignInPage />,
                },
              ],
            },
          ],
        },
        {
          path: '/',
          element: <Outlet />,
          children: [
            {
              element: <AuthorizedRoute />,
              children: [
                {
                  element: <Home withSettings={withSettings} />,
                  children: [
                    {
                      index: true,
                      element: <div></div>,
                    },
                    ...getRoutes(endpoints, '/'),
                  ],
                },
              ],
            },
          ],
        },
        {
          path: '*',
          element: <div>not found</div>,
        },
      ]),
    [endpoints]
  );

  return (
    <div dir={dir}>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: '#1f7983',
          },
        }}
        locale={lang === 'ar' ? ar : en}
        direction={dir}
      >
        <RouterProvider router={router} />
      </ConfigProvider>
    </div>
  );
};

export default MainApp;

interface HomeProps {
  withSettings?: boolean;
}

export const Home = ({ withSettings }: HomeProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname === '/') {
      navigate('/coordination-content');
    }
  }, [location.pathname, navigate]);

  return (
    <div className="h-screen flex overflow-hidden bg-white">
      <SideBar withSettings={withSettings} />
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        <NavBar />
        <main className="flex-1 flex-grow overflow-y-auto focus:outline-none  h-full">
          <div className=" h-full ">
            <AsyncComponent>
              <Outlet />
            </AsyncComponent>
          </div>
        </main>
      </div>
    </div>
  );
};
